import PropTypes from "prop-types";
import { useRef, useState, useEffect, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ClipLoader } from "react-spinners";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import Link from "next/link";
// api
// import { fetchParentServicesData } from "./http/commonApi";

const ServicesSection = memo(({ services, className = "" }) => {
  const { t } = useTranslation(["home", "common"]);
  const router = useRouter();
  const lang = router.locale || "en";
  const sectionRef = useRef(null);
  const containerRef = useRef(null);
  const scrollRef = useRef([]);
  const [isInView, setIsInView] = useState(false);
  const [paused, setPaused] = useState(false);
  // const [services, setServices] = useState([]);
  // const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);

  // useEffect(() => {
  //   const fetchServicesListData = async () => {
  //     try {
  //       setLoading(true);
  //       const { data } = await fetchParentServicesData(lang);
  //       setServices(data);
  //     } catch (error) {
  //       console.error("Error fetching Data:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchServicesListData();
  // }, [lang]);

  // Detect if the section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.2 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Auto-scroll when in view and not paused
  useEffect(() => {
    if (!services || services.length === 0 || !isInView || paused) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        const nextIndex = (prev + 1) % services.length;

        setTimeout(() => {
          scrollRef.current[nextIndex]?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }, 0);

        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [services, isInView, paused]);

  const handleServiceClick = (index) => {
    setActiveIndex(index);
    scrollRef.current[index]?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  // useEffect(() => {
  //   if (!services || services.length === 0) return;

  //   const interval = setInterval(() => {
  //     setActiveIndex((prev) => (prev + 1) % services.length);
  //   }, 5000);

  //   return () => clearInterval(interval);
  // }, [services]);

  return (
    <section
      ref={sectionRef}
      // onMouseEnter={() => setPaused(true)}
      // onMouseLeave={() => setPaused(false)}
      className={`mb-[40px] sm:mb-[40px] md:mb-[40px] lg:mb-[60px] xl:mb-[60px] w-full bg-text-primary text-background-color-primary   ${className}`}
    >
      <div className="w-full box-border flex flex-col gap-6 md:gap-10 px-[24px] md:px-[60px] lg:px-[80px] xl:px-[80px] py-[40px] md:py-[40px] lg:py-[60px] lg:py-[60px] max-w-[1600px] mx-auto">
        <h2 className="text-[28px] sm:text-[32px] lg:text-[40px] capitalize font-medium leading-[120%] text-start md:text-center m-0">
          {t("home:home.services.title")}
        </h2>
        {/* {loading ? (
          <div className="w-full flex justify-center items-center py-20">
            <ClipLoader color="#EF4444" size={48} />
          </div>
        ) : ( */}
        <div className="flex flex-col lg:flex-row gap-8 w-full items-center">
          {/* Left: Service Titles */}
          {/* <div
            ref={containerRef}
            className="overflow-x-auto lg:overflow-y-auto lg:overflow-x-hidden max-h-[400px] lg:max-h-[465px] px-4 flex flex-row lg:flex-col snap-x snap-mandatory gap-4 scroll-smooth custom_scrollbar"
          > */}
          <div
            ref={containerRef}
            className="flex overflow-x-hidden lg:overflow-y-auto gap-4 p-1 sm:px-4 scroll-smooth custom_scrollbar max-w-full lg:max-h-[465px] flex-row lg:flex-col snap-x snap-mandatory touch-pan-x"
          >
            {services?.map((service, index) => (
              <button
                key={index}
                ref={(el) => (scrollRef.current[index] = el)}
                onClick={() => handleServiceClick(index)}
                className={`w-max self-start shrink-0 cursor-pointer px-6 py-[12px]  text-start font-medium text-[16px] sm:text-[18px] lg:text-[20px] rounded-full text-left transition-all duration-300 bg-transparent text-white font-medium flex items-center justify-start box-border ${
                  activeIndex === index
                    ? "border border-crimson outline outline-1 outline-crimson ml-2"
                    : ""
                }`}
              >
                {/* {lang === "ar" ? service?.menuname_ar : service?.menuname} */}
                {service.name}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="hidden lg:block h-[465px] w-px bg-gray-200" />

          {/* Right: Service Content */}
          <div className="flex-1 flex flex-col gap-6 px-1 sm:px-3 overflow-hidden w-full">
            {Array.isArray(services) && services.length > 0 && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col gap-6"
                >
                  <h3 className=" font-medium text-[24px] sm:text-[30px] lg:text-[40px] leading-[120%] m-0">
                    {services[activeIndex]?.title}
                  </h3>
                  <div
                    className=" font-normal text-[14px] sm:text-[16px] leading-[150%] break-words whitespace-pre-wrap m-0 rich-text"
                    dangerouslySetInnerHTML={{
                      __html: services[activeIndex]?.description || "",
                    }}
                  />
                  {/* {services[activeIndex]?.description}
                  </p> */}
                </motion.div>
              </AnimatePresence>
            )}

            <div className="flex gap-4 flex-wrap">
              <button
                className="rounded-[50px] bg-white text-black text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-[#e72c46] hover:text-white transition duration-300 ease-in-out"
                onClick={() => router.push("/contact-digital-marketing-agency")}
              >
                {t("common:btnTxt.getInTouch")}
              </button>
              {services[activeIndex]?.slug && (
                <Link
                  href={`/${services[activeIndex]?.slug}`}
                  className="no-underline rounded-[50px] bg-transparent text-black text-white border border-white border-solid text-[16px] font-medium py-3 px-6 cursor-pointer hover:bg-white hover:text-black transition duration-300 ease-in-out"
                  aria-label={`Explore our ${services[activeIndex]?.name} services in Dubai`}
                >
                  {t("common:btnTxt.learnMore")}
                </Link>
              )}
            </div>
          </div>
        </div>
        {/* )} */}
      </div>
    </section>
  );
});

ServicesSection.propTypes = {
  className: PropTypes.string,
};

export default ServicesSection;
